import React from "react";
import {
  Box, Typography, Card, CardContent, CardActionArea, Chip, IconButton, useTheme, MobileStepper, Paper,
  ToggleButton, ToggleButtonGroup, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
  Tooltip, Modal, Fade, Divider, Snackbar, Alert,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AndroidIcon from "@mui/icons-material/Android";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import NavBar from "../../../components/navbar/navbar";
import { API_URL } from "../../../utils/common";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCatalogosBanners, TipoAplicacion, TipoBanner, } from "../../../hooks/catalogosBanners/catalogosbanners";

type Platform = "web" | "ios" | "android";
type StatusFilter = "all" | "active" | "scheduled" | "expired" | "inactive";

interface Banner {
  id: number;
  titulo: string;
  descripcion: string;

  // NUEVA ESTRUCTURA
  vista: string;
  tipoAplicacion: string;
  tipo: string;
  tipoVista: string;
  formato: string;
  seccion: string;

  urlBanner: string;
  urlRedirect: string;
  urlView: string;

  activo: boolean;

  createdAt?: string;
  updatedAt?: string;

  distribuciones?: {
    idEmpresa: number;
    empresaNombre?: string;
    idRegion: number;
    regionNombre?: string;
    redirectWeb: boolean;
    idEstado: number;
    estadoNombre?: string;
    externo: boolean;
    fechaInicio: string;
    fechaFin: string;
  }[];
}

const CmsDemo: React.FC = () => {
  const theme = useTheme();
  const [banners, setBanners] = React.useState<Banner[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeStep, setActiveStep] = React.useState(0);
  const [platform, setPlatform] = React.useState<Platform>("web");
  const [tipoFilter, setTipoFilter] = React.useState<string>("all");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [previewMedia, setPreviewMedia] = React.useState<Banner | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [vistaFilter, setVistaFilter] = React.useState<string>("all");

  // Catalogos dinamicos desde backend (mismo patron que cms.tsx),
  // en lugar de listas hardcodeadas. Asi apps nuevas (ej. SKY WEB)
  // aparecen sin tocar la vista.
  const { obtenerTiposAplicacion, obtenerTipos } = useCatalogosBanners();
  const [catalogoVistas, setCatalogoVistas] = React.useState<TipoAplicacion[]>([]);
  const [catalogoTipos, setCatalogoTipos] = React.useState<TipoBanner[]>([]);

  const normalizar = (valor?: string | null) => valor?.trim().toUpperCase() ?? "";
  // Normaliza el tipo de aplicacion: el backend puede devolver "IZZI_APPS" (slug)
  // mientras el catalogo del combo usa "IZZI APPS" (con espacio). Unificamos
  // espacios, guiones y guiones bajos para comparar sin falsos negativos.
  const normalizarApp = (valor?: string | null) =>
    normalizar(valor).replace(/[\s_-]+/g, " ");
  const esCarrusel = (banner: Banner) => normalizar(banner.tipoVista) === "CARRUSEL";
  const esEstatico = (banner: Banner) => normalizar(banner.tipoVista) === "ESTATICO";
  const esImagen = (banner: Banner) => normalizar(banner.formato) === "IMAGEN";
  const esVideo = (banner: Banner) => normalizar(banner.formato) === "VIDEO";
  const esTexto = (banner: Banner) => normalizar(banner.formato) === "TEXTO";

  const tiposDisponibles = vistaFilter !== "all" ? catalogoTipos : [];

  // Cargar tipos de aplicacion (combo "Vista")
  React.useEffect(() => {
    const cargarVistas = async () => {
      const resultado = await obtenerTiposAplicacion();
      setCatalogoVistas(resultado);
    };
    cargarVistas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar tipos dependientes de la aplicacion seleccionada
  React.useEffect(() => {
    const cargarTipos = async () => {
      if (vistaFilter === "all") {
        setCatalogoTipos([]);
        return;
      }
      const resultado = await obtenerTipos(vistaFilter);
      setCatalogoTipos(resultado);
    };
    cargarTipos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaFilter]);

  const [expanded, setExpanded] = React.useState<string | false>("Urgentes");
  const handleAccordion =
    (panel: string) =>
      (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
      };

  React.useEffect(() => {
    fetch(`${API_URL}/public/banners`)
      .then((r) => r.json())
      .then((data: Banner[]) => {
        setBanners(data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const getStatus = (b: Banner): { label: string; color: "success" | "warning" | "error" | "default" } => {
    if (!b.activo) return { label: "Inactivo", color: "default" };
    const now = new Date();
    const dist = b.distribuciones?.[0];
    if (dist?.fechaInicio && new Date(dist.fechaInicio) > now) return { label: "Programado", color: "warning" };
    if (dist?.fechaFin && new Date(dist.fechaFin) < now) return { label: "Expirado", color: "error" };
    return { label: "Activo", color: "success" };
  };

  //muestra de informcion solo activo 
  const isBannerVisible = (banner: Banner) => {
    if (!banner.activo) return false;

    const dist = banner.distribuciones?.[0];
    if (!dist) return false;

    const now = new Date();

    if (dist.fechaInicio && new Date(dist.fechaInicio) > now) {
      return false;
    }

    if (dist.fechaFin && new Date(dist.fechaFin) < now) {
      return false;
    }

    return true;
  };

  const getDaysUntilExpiry = (banner: Banner): number | null => {

    const dist = banner.distribuciones?.[0];
    if (!dist?.fechaFin) return null;

    const now = new Date();
    const fin = new Date(dist.fechaFin);

    const diff = Math.ceil(
      (fin.getTime() - now.getTime()) /
      (1000 * 60 * 60 * 24)
    );

    return diff;
  };


  type ExpiryFilter =
    | "all"
    | "today"
    | "7days"
    | "15days";


  const [expiryFilter, setExpiryFilter] =
    React.useState<ExpiryFilter>("all");

  const filterBanners = (list: Banner[]) => {

    return list.filter((b) => {

      // SOLO BANNERS VISIBLES
      if (!isBannerVisible(b)) {
        return false;
      }

      // FILTRO VISTA (por tipo de aplicacion)
      // El backend expone el tipo de aplicacion en b.tipoAplicacion
      // (b.vista viene null). Comparamos con normalizarApp para tolerar
      // "IZZI_APPS" vs "IZZI APPS".
      if (
        vistaFilter !== "all" &&
        normalizarApp(b.tipoAplicacion) !== normalizarApp(vistaFilter)
      ) {
        return false;
      }



      // FILTRO TIPO


      if (
        tipoFilter !== "all" &&
        normalizar(b.tipo) !== normalizar(tipoFilter)
      ) {
        return false;
      }



      // VENCIMIENTO


      const days =
        getDaysUntilExpiry(b);

      if (expiryFilter !== "all") {

        if (days === null) {
          return false;
        }

        switch (expiryFilter) {

          case "today":

            if (days !== 0) {
              return false;
            }

            break;


          case "7days":

            if (
              days < 0 ||
              days > 7
            ) {
              return false;
            }

            break;


          case "15days":

            if (
              days < 0 ||
              days > 15
            ) {
              return false;
            }

            break;
        }
      }



      // BÚSQUEDA


      if (searchQuery) {

        const q =
          searchQuery
            .trim()
            .toLowerCase();

        const titulo =
          b.titulo?.toLowerCase() ?? "";

        const descripcion =
          b.descripcion?.toLowerCase() ?? "";

        const tipo =
          b.tipo?.toLowerCase() ?? "";

        if (
          !titulo.includes(q) &&
          !descripcion.includes(q) &&
          !tipo.includes(q)
        ) {
          return false;
        }
      }


      return true;
    });
  };

  const carruselBanners = filterBanners(
    banners.filter((banner) => {
      if (!esCarrusel(banner)) {
        return false;
      }

      // TEXTO no necesita archivo
      if (esTexto(banner)) {
        return true;
      }

      // IMAGEN / VIDEO necesitan urlBanner
      return Boolean(banner.urlBanner);
    })
  );

  const bannersEstaticos = filterBanners(
    banners.filter((banner) =>
      esEstatico(banner)
    )
  );


  const bannersPorTipo = Object.entries(
    filterBanners(banners).reduce((acc, banner) => {
      const tipo = banner.tipo || "Sin tipo";

      if (!acc[tipo]) {
        acc[tipo] = [];
      }

      acc[tipo].push(banner);

      return acc;
    }, {} as Record<string, Banner[]>)
  );
  //const listaBanners = filterBanners(banners);
  //const noticiasBanners = filterBanners(banners.filter((b) => b.tipo === "noticias"));
  //const urgentBanners = filterBanners(banners.filter((b) => b.tipo === "urgente"));
  const maxSteps = carruselBanners.length;
  //redireccion de url
  const currentBanner = carruselBanners[activeStep];
  const currentDist = currentBanner?.distribuciones?.[0];
  const hasRedirect = currentDist?.redirectWeb && currentBanner?.urlRedirect;

  React.useEffect(() => {
    if (maxSteps <= 1 || previewMedia) {
      return;
    }

    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }, 15000);

    return () => clearInterval(timer);
  }, [maxSteps, previewMedia]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setActiveStep((prev) => Math.min(prev + 1, maxSteps - 1));
      if (e.key === "ArrowLeft") setActiveStep((prev) => Math.max(prev - 1, 0));
      if (e.key === "Escape") {
        setPreviewMedia(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [maxSteps]);

  const tipoColor = (tipo: string): "primary" | "secondary" | "error" | "default" => {
    switch (tipo) {
      case "banner": return "primary";
      case "noticias": return "secondary";
      case "urgente": return "error";
      default: return "default";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ open: true, message: "URL copiada al portapapeles", severity: "success" });
    }).catch(() => {
      setSnackbar({ open: true, message: "Error al copiar", severity: "error" });
    });
  };

  const exportConfig = () => {
    const config = banners.map((b) => ({
      id: b.id,
      titulo: b.titulo,
      tipo: b.tipo,
      tipoVista: b.tipoVista,
      urlBanner: b.urlBanner,
      urlRedirect: b.urlRedirect,
      status: getStatus(b).label,
    }));
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cms-banners-config-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: "Configuracion exportada", severity: "success" });
  };

  const getPlatformStyles = () => {
    switch (platform) {
      case "ios":
        return {
          container: { width: 375, mx: "auto", border: "8px solid #1a1a1a", borderRadius: 40, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" },
          carouselHeight: 200,
          bannerHeight: 50,
          cardWidth: "100%",
        };
      case "android":
        return {
          container: { width: 360, mx: "auto", border: "6px solid #333", borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" },
          carouselHeight: 180,
          bannerHeight: 50,
          cardWidth: "100%",
        };
      default:
        return {
          container: { width: "100%", maxWidth: 800, mx: "auto" },
          carouselHeight: 300,
          bannerHeight: 90,
          cardWidth: "48%",
        };
    }
  };

  const platformStyles = getPlatformStyles();

  const BannerMedia = ({
    banner,
    search = "",
  }: {
    banner: Banner;
    search?: string;
  }) => {


    // IMAGEN
    if (esImagen(banner)) {
      return (
        <Box
          component="img"
          src={banner.urlBanner}
          alt={banner.titulo}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      );
    }

    // VIDEO
    if (esVideo(banner)) {
      return (
        <Box
          component="video"
          src={banner.urlBanner}
          autoPlay
          muted
          loop
          playsInline
          controls
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            bgcolor: "#000",
            display: "block",
          }}
        />
      );
    }


    // TEXTO
    if (esTexto(banner)) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 4,
            textAlign: "center",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
            >
              {highlightText(
                banner.titulo,
                search
              )}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {highlightText(
                banner.titulo,
                search
              )}
            </Typography>
          </Box>
        </Box>
      );
    }

    // SIN CONTENIDO

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">
          Sin contenido
        </Typography>
      </Box>
    );
  };

  const getExpiryColor = (days: number): "success" | "warning" | "error" => {

    if (days <= 7) {
      return "error";      //  rojo -- expira en menos de 7 dias
    }

    if (days <= 30) {
      return "warning";    // naranja --  expira en menos de 30 dias
    }

    return "success";      // verde --  expira en mas de un mes 
  };

  const StatusChip = ({ banner }: { banner: Banner }) => {

    const status = getStatus(banner);
    const days = getDaysUntilExpiry(banner);

    return (
      <Box display="flex" gap={0.5} alignItems="center">

        <Chip
          label={status.label}
          color={status.color}
          size="small"
        />

        {days !== null && (
          <Chip
            label={`Expira en ${days} días`}
            color={getExpiryColor(days)}
            size="small"
            variant="filled"
          />
        )}

      </Box>
    );
  };

  const DistributionInfo = ({ banner }: { banner: Banner }) => {
    const dist = banner.distribuciones?.[0];
    if (!dist) return <Chip label="Sin distribucion" size="small" variant="outlined" />;
    const regions = dist.regionNombre || "Nacional";
    const externo = dist.externo === true ? "Externo" : dist.externo === false ? "Interno" : "Todos";
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <Chip label={regions} size="small" variant="outlined" />
        <Chip label={externo} size="small" variant="outlined" />
      </Box>
    );
  };

  const BannerCard = ({
    banner,
    showImage = false,
  }: {
    banner: Banner;
    showImage?: boolean;
  }) => {

    const tieneArchivo =
      (esImagen(banner) || esVideo(banner)) &&
      Boolean(banner.urlBanner);

    return (
      <Card
        sx={{
          flex:
            platform === "web"
              ? "1 1 300px"
              : "1 1 100%",

          maxWidth: platformStyles.cardWidth,

          borderLeft: 4,
          borderColor: "grey.400",

          transition:
            "transform 0.2s, box-shadow 0.2s",

          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 3,
          },

          overflow: "hidden",
        }}
      >

        {/* ========================================== */}
        {/* CONTENIDO VISUAL */}
        {/* ========================================== */}

        {showImage && (
          <Box
            sx={{
              height:
                esTexto(banner)
                  ? "auto"
                  : platformStyles.bannerHeight,

              minHeight:
                esTexto(banner)
                  ? 100
                  : undefined,

              position: "relative",
              overflow: "hidden",
              bgcolor: theme.palette.grey[100],
            }}
          >

            {/* IMAGEN / VIDEO */}

            {tieneArchivo && (
              <BannerMedia
                banner={banner}
                search={searchQuery}
              />
            )}


            {/* TEXTO */}

            {esTexto(banner) && (
              <BannerMedia
                banner={banner}
                search={searchQuery}
              />
            )}


            {/* SIN ARCHIVO */}

            {!esTexto(banner) && !tieneArchivo && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Sin Archivo
                </Typography>
              </Box>
            )}

          </Box>
        )}


        {/* ========================================== */}
        {/* INFORMACIÓN */}
        {/* ========================================== */}

        <CardContent>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
            gap={1}
          >

            <Chip
              label={banner.tipo}
              size="small"
              variant="outlined"
            />

            <StatusChip banner={banner} />

          </Box>


          {/* Evitamos repetir texto si ya se mostró arriba */}

          {!esTexto(banner) && (
            <>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
              >
                {highlightText(
                  banner.titulo,
                  searchQuery
                )}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {highlightText(
                  banner.descripcion,
                  searchQuery
                )}
              </Typography>
            </>
          )}


          {/* TIPO DE CONTENIDO */}

          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
            mb={1}
          >

            <Chip
              label={banner.seccion}
              size="small"
              variant="outlined"
            />

            <Chip
              label={banner.tipoVista}
              size="small"
              variant="outlined"
            />

          </Box>


          {/* DISTRIBUCIÓN */}

          <DistributionInfo banner={banner} />


          {/* ========================================== */}
          {/* URL REDIRECT */}
          {/* TEXTO NO DEBE MANEJAR URL */}
          {/* ========================================== */}

          {!esTexto(banner) && banner.urlRedirect && (

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mt={1}
            >

              <Typography
                variant="caption"
                color="primary"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                → {banner.urlRedirect}
              </Typography>


              <Tooltip title="Abrir en nueva pestaña">

                <IconButton
                  size="small"
                  onClick={() =>
                    window.open(
                      banner.urlRedirect,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>

              </Tooltip>


              <Tooltip title="Copiar URL">

                <IconButton
                  size="small"
                  onClick={() =>
                    copyToClipboard(
                      banner.urlRedirect
                    )
                  }
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>

              </Tooltip>

            </Box>

          )}

        </CardContent>

      </Card>
    );
  };
  const BannerSection = ({
    title,
    banners,
    showImage = false,
  }: {
    title: string;
    banners: Banner[];
    showImage?: boolean;
  }) => (
    <Accordion
      expanded={expanded === title}
      onChange={handleAccordion(title)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {title}
          </Typography>

          <Chip
            label={`${banners.length} registros`}
            color="primary"
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails>

        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          justifyContent={
            platform !== "web"
              ? "center"
              : "flex-start"
          }
        >

          {banners.map((banner) => (
            <BannerCard
              key={banner.id}
              banner={banner}
              showImage={showImage}
            />
          ))}

        </Box>

      </AccordionDetails>

    </Accordion>
  );

  const handleCarouselClick = () => {

    const banner =
      carruselBanners[activeStep];

    if (!banner) {
      return;
    }

    // TEXTO no abre modal multimedia
    if (esTexto(banner)) {
      return;
    }

    // IMAGEN / VIDEO necesitan archivo
    if (!banner.urlBanner) {
      return;
    }

    setPreviewMedia(banner);
  };

  // carrusel regresar a al reiniciar carga con los filtros seleccionados 
  React.useEffect(() => {
    setActiveStep(0);
  }, [
    searchQuery,
    tipoFilter,
    expiryFilter
  ]);

  // pintado de palabras oara el buscador
  const highlightText = (
    text: string | undefined | null,
    search: string
  ) => {

    if (!text) {
      return "";
    }

    if (!search.trim()) {
      return text;
    }

    // Escapar caracteres especiales de RegExp
    const escapedSearch = search.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const regex = new RegExp(
      `(${escapedSearch})`,
      "ig"
    );

    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {

          const coincide =
            part.toLowerCase() ===
            search.toLowerCase();

          if (coincide) {
            return (
              <Box
                component="span"
                key={index}
                sx={{
                  backgroundColor: "#FFEB3B",
                  color: "#000",
                  fontWeight: "bold",
                  px: 0.3,
                  borderRadius: "2px",
                }}
              >
                {part}
              </Box>
            );
          }

          return part;
        })}
      </>
    );
  };


  return (
    <Box width="100%" mb={8}>
      <NavBar />
      <Box px={4} pt={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h4" fontWeight="bold">
            Demo CMS — Vista Previa
          </Typography>
          {/*
          <Box display="flex" gap={1}>
            <Tooltip title="Exportar configuracion">
              <IconButton onClick={exportConfig}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
          */}
        </Box>
        <Typography variant="body1" color="text.secondary" mb={2}>
          Vista previa banners y carruseles
        </Typography>

        <Box display="flex" justifyContent="center" mb={2}>
          <ToggleButtonGroup value={platform} exclusive onChange={(_, v) => v && (setPlatform(v), setActiveStep(0))} size="small">
            <ToggleButton value="web"><DesktopWindowsIcon sx={{ mr: 0.5 }} /> Web</ToggleButton>
            <ToggleButton value="ios"><PhoneIphoneIcon sx={{ mr: 0.5 }} /> iOS</ToggleButton>
            <ToggleButton value="android"><AndroidIcon sx={{ mr: 0.5 }} /> Android</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box display="flex" gap={2} mb={3} flexWrap="wrap" justifyContent="center">
          <TextField size="small" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: 200 }} />

          {/* FILTRO VISTA */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
            }}
          >
            <InputLabel>
              Vista
            </InputLabel>

            <Select
              value={vistaFilter}
              label="Vista"
              onChange={(e: SelectChangeEvent) => {

                const nuevaVista =
                  e.target.value;

                setVistaFilter(nuevaVista);

                // Al cambiar Vista limpiamos Tipo
                setTipoFilter("all");
              }}
            >
              <MenuItem value="all">
                Todas
              </MenuItem>

              {catalogoVistas.map((vista) => (
                <MenuItem
                  key={vista.id ?? vista.nombre}
                  value={vista.nombre}
                >
                  {vista.nombre}
                </MenuItem>
              ))}

            </Select>
          </FormControl>

          {/* FILTRO TIPO */}
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
            }}
            disabled={vistaFilter === "all"}
          >
            <InputLabel>
              Tipo
            </InputLabel>

            <Select
              value={tipoFilter}
              label="Tipo"
              onChange={(e: SelectChangeEvent) =>
                setTipoFilter(
                  e.target.value
                )
              }
            >

              <MenuItem value="all">
                Todos
              </MenuItem>

              {tiposDisponibles.map((tipo) => (
                <MenuItem
                  key={`${tipo.tipoAplicacion}-${tipo.nombre}`}
                  value={tipo.nombre}
                >
                  {tipo.nombre}
                </MenuItem>
              ))}

            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={expiryFilter}
              label="Vencimiento"
              onChange={(e: SelectChangeEvent) =>
                setExpiryFilter(e.target.value as ExpiryFilter)
              }
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="today">Expira hoy</MenuItem>
              <MenuItem value="7days">Expira en 7 días</MenuItem>
              <MenuItem value="15days">Expira en 15 días</MenuItem>
            </Select>
          </FormControl>

        </Box>

        {isLoading ? (
          <Typography textAlign="center" py={4}>Cargando banners...</Typography>
        ) : banners.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">No hay banners configurados aun</Typography>
          </Paper>
        ) : (
          <>
            {/*
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" fontWeight="bold" mb={1}>Resumensss</Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip label={`Total: ${banners.length}`} />
                <Chip label={`Activos: ${banners.filter((b) => getStatus(b).label === "Activo").length}`} color="success" variant="outlined" />
                <Chip label={`Expirados: ${banners.filter((b) => getStatus(b).label === "Expirado").length}`} color="error" variant="outlined" />
               <<Chip label={`Programados: ${banners.filter((b) => getStatus(b).label === "Programado").length}`} color="warning" variant="outlined" />
                Chip label={`Carrusel: ${carruselBanners.length}`} color="primary" variant="outlined" />
                <Chip label={`Noticias: ${noticiasBanners.length}`} color="secondary" variant="outlined" />
              </Box>
            </Paper>
            */}

            {carruselBanners.length > 0 && (
              <Box mb={4}>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Carrusel ({carruselBanners.length} slides)
                </Typography>

                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: platform === "web" ? 3 : 0,
                    overflow: "hidden",
                    ...platformStyles.container,
                  }}
                >
                  <Box
                    sx={{
                      height: platformStyles.carouselHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: theme.palette.grey[100],
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >

                    {/* CONTENIDO DEL BANNER */}
                    {/* IMAGEN / VIDEO / TEXTO */}

                    {currentBanner && (
                      <BannerMedia
                        banner={currentBanner}
                      />
                    )}

                    {/* INFORMACIÓN */}
                    {/* En TEXTO no la repetimos */}

                    {currentBanner && !esTexto(currentBanner) && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,.6)",
                          color: "white",
                          p: 2,
                          zIndex: 2,
                        }}
                      >
                        <Typography variant="h6">
                          {currentBanner.titulo}
                        </Typography>

                        <Typography variant="body2">
                          {currentBanner.descripcion}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                    py={1}
                    bgcolor="white"
                  >

                    {/* Flecha izquierda */}
                    <IconButton
                      size="small"
                      onClick={() => setActiveStep((p) => Math.max(p - 1, 0))}
                      disabled={activeStep === 0}
                    >
                      <KeyboardArrowLeft />
                    </IconButton>

                    {/* Indicadores clickeables */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.8}
                    >
                      {carruselBanners.map((_, index) => (
                        <Box
                          key={index}
                          onClick={() => setActiveStep(index)}
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor:
                              index === activeStep
                                ? theme.palette.primary.main
                                : "#bdbdbd",
                            cursor: "pointer",
                            transition: "all .25s ease",

                            "&:hover": {
                              transform: "scale(1.4)",
                              bgcolor: theme.palette.primary.main,
                            },
                          }}
                        />
                      ))}
                    </Box>

                    {/* Flecha derecha */}
                    <IconButton
                      size="small"
                      onClick={() => setActiveStep((p) => Math.min(p + 1, maxSteps - 1))}
                      disabled={activeStep === maxSteps - 1}
                    >
                      <KeyboardArrowRight />
                    </IconButton>

                  </Box>
                </Paper>

                {currentBanner && (
                  <Box mt={1}>
                    <StatusChip banner={currentBanner} />
                  </Box>
                )}
              </Box>
            )}

            {bannersPorTipo.map(([tipo, lista]) => (
              <BannerSection
                key={tipo}
                title={
                  tipo.charAt(0).toUpperCase() +
                  tipo.slice(1).toLowerCase()
                }
                banners={lista}
                showImage
              />
            ))}


          </>
        )}
      </Box>


      {/* MODAL VISTA PREVIA - IMAGEN / VIDEO */}

      <Modal
        open={Boolean(previewMedia)}
        onClose={() => setPreviewMedia(null)}
      >
        <Fade in={Boolean(previewMedia)}>

          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",

              width: "auto",
              maxWidth: "90vw",
              maxHeight: "90vh",

              bgcolor: "black",
              borderRadius: 2,
              overflow: "hidden",

              outline: "none",
            }}
          >


            {/* REDIRECCION */}

            {previewMedia?.urlRedirect && (
              <Box
                onClick={() =>
                  window.open(
                    previewMedia.urlRedirect,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,

                  zIndex: 20,

                  px: 2,
                  py: 1,

                  borderRadius: 2,

                  bgcolor: "rgba(255,255,255,.12)",
                  color: "white",

                  cursor: "pointer",

                  display: "flex",
                  alignItems: "center",
                  gap: 1,

                  transition: ".2s",

                  "&:hover": {
                    bgcolor: "primary.main",
                  },
                }}
              >
                <OpenInNewIcon fontSize="small" />

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  Redirigir a URL
                </Typography>
              </Box>
            )}



            {/* CERRAR */}


            <IconButton
              onClick={() =>
                setPreviewMedia(null)
              }
              sx={{
                position: "absolute",
                top: 8,
                right: 8,

                color: "white",

                zIndex: 20,

                bgcolor: "rgba(0,0,0,.5)",

                "&:hover": {
                  bgcolor: "rgba(0,0,0,.7)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* IMAGEN */}
            {previewMedia &&
              esImagen(previewMedia) &&
              previewMedia.urlBanner && (

                <Box
                  component="img"

                  src={
                    previewMedia.urlBanner
                  }

                  alt={
                    previewMedia.titulo ||
                    "Vista previa"
                  }

                  sx={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",

                    width: "auto",
                    height: "auto",

                    display: "block",

                    objectFit: "contain",
                  }}
                />

              )}

            {/* VIDEO */}

            {previewMedia &&
              esVideo(previewMedia) &&
              previewMedia.urlBanner && (

                <Box
                  component="video"

                  src={
                    previewMedia.urlBanner
                  }

                  controls
                  autoPlay
                  playsInline

                  sx={{
                    width: "90vw",
                    maxWidth: 1000,

                    maxHeight: "85vh",

                    display: "block",

                    objectFit: "contain",

                    bgcolor: "#000",
                  }}
                />

              )}

          </Box>

        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CmsDemo;
